import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PageErrorBoundaryProps {
  /** Changing this clears the error, so navigating away from a broken page recovers. */
  resetKey: string;
  children: ReactNode;
}

interface PageErrorBoundaryState {
  error: Error | null;
  resetKey: string;
}

/**
 * Catches a render crash so it costs one page, not the whole app. Before this there was no
 * boundary anywhere: any page that threw unmounted everything above it -- sidebar included --
 * and left a blank screen until a full reload. React still reports the caught error to the
 * console, so nothing is swallowed.
 */
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<PageErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: PageErrorBoundaryProps,
    state: PageErrorBoundaryState,
  ): Partial<PageErrorBoundaryState> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  override render() {
    if (this.state.error == null) return this.props.children;
    return (
      <div role="alert" className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
        <AlertTriangle size={28} className="text-amber-500" aria-hidden="true" />
        <h2 className="text-base font-semibold text-brand-text-primary">This page hit a problem</h2>
        <p className="max-w-sm text-sm text-brand-text-secondary">
          Nothing was lost, and the rest of the app still works. Try again, or reload if it keeps happening.
        </p>
        <div className="mt-1 flex gap-2">
          <Button variant="primary" size="sm" onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
          <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
